/**
 * ScreenError (DNKADM-0440) — the one renderer for a screen's (or an in-screen
 * panel's) failed load, driven by `toScreenError` (`src/api/screenError.ts`):
 *
 *  - `401` → the session line + Sign in (the existing centralized-login redirect);
 *  - `403` → the DNKADM-0433 `AccessDeniedNotice`: why (the screen's own role copy,
 *    else the server's reason, else the shared fallback) and who to ask, NO retry;
 *  - `404` → the screen's own not-found state when it has one, else a plain notice;
 *  - `400`/`409`/`422` → the server's message (Retry kept: the state may have moved);
 *  - `5xx`/network → "Couldn't load {what}. Please try again." + Retry.
 *
 * `variant="alert"` is the MUI Alert most list/detail screens used; `variant="panel"`
 * is the heading + line + outlined button the roster and ledger screens used.
 */
import type { ReactNode } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { toScreenError } from '@/api/screenError';
import { AccessDeniedNotice } from '@/components/AccessDeniedNotice';
import { ACCESS_DENIED_FALLBACK, SESSION_EXPIRED_MESSAGE } from '@/components/accessCopy';
import { redirectToCentralizedLogin } from '@/auth/silentRefresh';
import { Icon } from '@/theme';

export interface ScreenErrorProps {
  /** The thrown load failure. */
  error: unknown;
  /** What failed to load, as it reads in a sentence: "the locations". */
  what: string;
  /** Re-run the load. Omitted → no retry control. Never shown on a `403`. */
  onRetry?: () => void;
  /** The screen's own "why and who to ask" for a `403` (wins over the server's reason). */
  forbiddenMessage?: string | undefined;
  /** The screen's own not-found state for a `404` (e.g. a detail screen's back link). */
  notFound?: ReactNode;
  /** The screen's own line for a `5xx`/network failure (default "Couldn't load {what}…"). */
  retryMessage?: string | undefined;
  /** The screen's own line for a rejected (`400`/`409`/`422`) load with no server message. */
  rejectedMessage?: string | undefined;
  /**
   * Show the server's (scrubbed) message on a `5xx`/network failure too, ahead of
   * `retryMessage` — for screens whose upstream text names what is down.
   */
  preferServerMessage?: boolean;
  /** `panel`: heading + line + outlined button. Default `alert`. */
  variant?: 'alert' | 'panel';
  /** `panel` heading for the retry/rejected states (default "Couldn't load {what}"). */
  title?: string | undefined;
  /** `panel` heading for a rejected (`400`/`409`/`422`) load only (default `title`). */
  rejectedTitle?: string | undefined;
  /** Retry button label (default "Retry"). */
  retryLabel?: string;
  sx?: SxProps<Theme> | undefined;
}

export function ScreenError({
  error,
  what,
  onRetry,
  forbiddenMessage,
  notFound,
  retryMessage,
  rejectedMessage,
  preferServerMessage = false,
  variant = 'alert',
  title,
  rejectedTitle,
  retryLabel = 'Retry',
  sx,
}: ScreenErrorProps) {
  const state = toScreenError(error);
  // `exactOptionalPropertyTypes`: never hand MUI an explicit `sx={undefined}`.
  const sxProp = sx === undefined ? {} : { sx };

  if (state.kind === 'forbidden') {
    return (
      <Box {...sxProp}>
        <AccessDeniedNotice message={forbiddenMessage ?? state.message ?? ACCESS_DENIED_FALLBACK} />
      </Box>
    );
  }

  if (state.kind === 'session') {
    return (
      <Alert
        severity="warning"
        role="alert"
        {...sxProp}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() =>
              redirectToCentralizedLogin(window.location.pathname + window.location.search)
            }
          >
            Sign in
          </Button>
        }
      >
        {SESSION_EXPIRED_MESSAGE}
      </Alert>
    );
  }

  if (state.kind === 'notFound' && notFound !== undefined) return <>{notFound}</>;
  if (state.kind === 'notFound') {
    return (
      <Alert severity="warning" role="alert" {...sxProp}>
        {state.message ?? `Couldn’t find ${what}. It may have been removed.`}
      </Alert>
    );
  }

  const fallback = retryMessage ?? `Couldn’t load ${what}. Please try again.`;
  const message =
    state.kind === 'rejected'
      ? (state.message ?? rejectedMessage ?? fallback)
      : preferServerMessage
        ? (state.message ?? fallback)
        : fallback;

  if (variant === 'panel') {
    return (
      <Box role="alert" {...sxProp}>
        <Typography variant="h3" component="h2">
          {(state.kind === 'rejected' ? rejectedTitle : undefined) ?? title ?? `Couldn’t load ${what}`}
        </Typography>
        <Typography sx={{ mt: 0.5, maxWidth: 520 }}>{message}</Typography>
        {onRetry ? (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Icon name="refresh" />}
            sx={{ mt: 1.5 }}
            onClick={onRetry}
          >
            {retryLabel}
          </Button>
        ) : null}
      </Box>
    );
  }

  return (
    <Alert
      severity="error"
      role="alert"
      {...sxProp}
      {...(onRetry
        ? {
            action: (
              <Button color="inherit" size="small" onClick={onRetry}>
                {retryLabel}
              </Button>
            ),
          }
        : {})}
    >
      {message}
    </Alert>
  );
}
